import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiGet } from 'hooks/useApi';
import SessionContext from 'contexts/session';
import Customer from 'models/Customer';
import Pagination from 'models/Pagination';

const ACTIVE_CUSTOMER_STORAGE_KEY = 'activeCustomerId';

// Prefixos de rotas que NÃO dependem do customer (não devem ser resetadas)
const CUSTOMER_INDEPENDENT_PREFIXES = ['/admin/', '/auth/'];

interface AdminCustomerContextType {
  activeCustomer: Customer | null;
  setActiveCustomer: (customer: Customer | null) => void;
  clearActiveCustomer: () => void;
  isAdmin: boolean;
  hasCustomerContext: boolean;
  customers: Customer[];
  isLoadingCustomers: boolean;
}

export const AdminCustomerContext = createContext<AdminCustomerContextType>({
  activeCustomer: null,
  setActiveCustomer: () => {},
  clearActiveCustomer: () => {},
  isAdmin: false,
  hasCustomerContext: false,
  customers: [],
  isLoadingCustomers: false
});

export const useAdminCustomer = () => useContext(AdminCustomerContext);

export function AdminCustomerProvider({ children }: PropsWithChildren<object>) {
  const { user } = useContext(SessionContext);
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'ADMIN';

  const [activeCustomer, setActiveCustomerState] = useState<Customer | null>(
    null
  );

  /**
   * Invalida todas as queries que dependem do customer.
   * Queries que começam com prefixos independentes (admin, auth) não são invalidadas.
   */
  const invalidateCustomerDependentQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        // O primeiro elemento da queryKey geralmente é o path da API
        if (queryKey.length > 0 && typeof queryKey[0] === 'string') {
          const path = queryKey[0];
          // Não invalida queries que são independentes do customer
          const isIndependent = CUSTOMER_INDEPENDENT_PREFIXES.some((prefix) =>
            path.startsWith(prefix)
          );
          return !isIndependent;
        }
        // Na dúvida, invalida
        return true;
      }
    });
  }, [queryClient]);

  // Fetch available customers for admin (get all with large page size)
  const { data: customersPage, isLoading: isLoadingCustomers } = useApiGet<
    Pagination<Customer>
  >('/admin/customers', {
    enabled: isAdmin,
    params: { pageSize: 100, page: 1 }
  });

  const customers = useMemo(() => customersPage?.items || [], [
    customersPage?.items
  ]);

  // Load customer from session storage on mount
  useEffect(() => {
    if (isAdmin && typeof window !== 'undefined' && customers.length > 0) {
      const storedCustomerId = window.sessionStorage?.getItem(
        ACTIVE_CUSTOMER_STORAGE_KEY
      );
      if (storedCustomerId) {
        const customer = customers.find(
          (c) => String(c.id) === storedCustomerId
        );
        if (customer) {
          setActiveCustomerState(customer);
        }
      }
    }
  }, [isAdmin, customers]);

  const setActiveCustomer = useCallback(
    (customer: Customer | null) => {
      const previousCustomerId = activeCustomer?.id;
      const newCustomerId = customer?.id;

      setActiveCustomerState(customer);

      if (typeof window !== 'undefined') {
        if (customer) {
          window.sessionStorage?.setItem(
            ACTIVE_CUSTOMER_STORAGE_KEY,
            customer.id
          );
        } else {
          window.sessionStorage?.removeItem(ACTIVE_CUSTOMER_STORAGE_KEY);
        }
      }

      // Invalida todas as queries que dependem do customer quando há mudança
      if (previousCustomerId !== newCustomerId) {
        invalidateCustomerDependentQueries();
      }
    },
    [activeCustomer?.id, invalidateCustomerDependentQueries]
  );

  const clearActiveCustomer = useCallback(() => {
    const hadCustomer = activeCustomer !== null;

    setActiveCustomerState(null);
    if (typeof window !== 'undefined') {
      window.sessionStorage?.removeItem(ACTIVE_CUSTOMER_STORAGE_KEY);
    }

    // Invalida todas as queries que dependem do customer
    if (hadCustomer) {
      invalidateCustomerDependentQueries();
    }
  }, [activeCustomer, invalidateCustomerDependentQueries]);

  const hasCustomerContext = isAdmin ? activeCustomer !== null : true;

  return (
    <AdminCustomerContext.Provider
      value={{
        activeCustomer,
        setActiveCustomer,
        clearActiveCustomer,
        isAdmin,
        hasCustomerContext,
        customers,
        isLoadingCustomers
      }}
    >
      {children}
    </AdminCustomerContext.Provider>
  );
}

export default AdminCustomerContext;
